import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  CreateCounterAPI,
  UpdateCounterAPI,
  GetAllDepartment,
} from "../../../lib/api";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { RouteNavigation } from "../../../Routes";
import Cookies from "js-cookie";
import { useState } from "react";
import React, { useEffect } from "react";

const CreateCounter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const counterData = location?.state?.CounterData;
  const counterId = counterData?.CounterID;
  const [isLoading, setIsLoading] = useState(false);
  const [showInactiveDepartmentWarning, setShowInactiveDepartmentWarning] =
    useState(false);

  const { data: departmentsData } = useQuery({
    queryKey: ["GetAllDepartment"],
    queryFn: () => GetAllDepartment(),
  });

  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      counterNumber: counterData?.CounterName || "",
      depId: counterData?.DepId || "",
      status: counterData?.Status ?? true,
    },
  });

  // Watch department selection
  const selectedDepartmentId = watch("depId");

  const mutate = useMutation({
    mutationFn: (data) => UpdateCounterAPI(data, counterId),
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: () => {
      toast.success("Counter updated successfully", {
        position: "top-right",
        closeOnClick: true,
      });
      navigate(RouteNavigation.counters);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update counter", {
        position: "top-right",
        closeOnClick: true,
      });
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const handleCounterSubmit = async (data) => {
    setIsLoading(true);
    try {
      const dataResponse = await CreateCounterAPI(data);

      if (dataResponse.status === 201) {
        toast.success("Counter created successfully", {
          position: "top-right",
          closeOnClick: true,
        });
        navigate(RouteNavigation.counters);
      } else {
        toast.error(dataResponse.error || "Failed to create counter", {
          position: "top-right",
          closeOnClick: true,
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 500) {
        toast.error("Counter already exists. Please use a different name.", {
          position: "top-right",
          closeOnClick: true,
        });
      } else {
        toast.error("An unexpected error occurred. Please try again.", {
          position: "top-right",
          closeOnClick: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (counterData) {
      // Check if the current department is inactive
      const currentDepartment = departmentsData?.data?.department?.find(
        (dept) => dept.DepID === counterData.DepID
      );
      setValue("depId", currentDepartment?.DepID);
      if (currentDepartment && !currentDepartment.Status) {
        setShowInactiveDepartmentWarning(true);
      }
    }
    setFocus("counterNumber");
  }, [counterData, setFocus, departmentsData]);

  const onSubmit = async (data) => {
    const CompanyId = Cookies.get("CompanyId");
    const payload = {
      ...data,
      companyId: CompanyId,
      depId: data.depId,
      counterName: data.counterNumber,
      status: data.status,
    };

    if (counterData) {
      mutate.mutate(payload);
    } else {
      handleCounterSubmit(payload);
    }
  };

  // Handle department selection change
  const handleDepartmentChange = (e) => {
    const selectedDeptId = e.target.value;
    setValue("depId", selectedDeptId);

    // Check if selected department is inactive
    const selectedDept = departmentsData?.data?.department?.find(
      (dept) => dept.DepId === selectedDeptId
    );
    setShowInactiveDepartmentWarning(selectedDept && !selectedDept.Status);
  };
  return (
    <div className="py-5">
      <h4 className="text-3xl font-bold mb-5">
        {counterData ? "Update" : "Create"} Counter
      </h4>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col w-full mt-5"
      >
        <div className="flex gap-10">
          <div className="w-full">
            <label className="font-semibold">Counter Name</label>
            <input
              className="flex items-center h-12 px-4 w-full bg-gray-200 mt-2 rounded focus:outline-none focus:ring-2"
              {...register("counterNumber", {
                required: "Counter name is required",
              })}
            />
            {errors.counterNumber && (
              <span className="text-red-600">
                {errors.counterNumber.message}
              </span>
            )}
          </div>
          <div className="w-full">
            <label className="font-semibold">Department</label>
            <select
              {...register("depId", {
                required: "Department is required",
                onChange: handleDepartmentChange,
              })}
              className="border-[1px] p-2 w-full h-12 mt-2 rounded-lg focus:outline-none focus:ring-2 bg-gray-200"
            >
              <option value="" disabled>
                Select Department
              </option>
              {departmentsData?.data?.department?.map((dept) => (
                <option
                  key={dept.DepID}
                  value={dept.DepID}
                  disabled={!dept.Status && dept.DepID !== counterData?.DepID}
                >
                  {dept.DepName} {!dept.Status && "(Inactive)"}
                </option>
              ))}
            </select>
            {errors.depId && (
              <p className="text-red-600">{errors.depId.message}</p>
            )}
            {showInactiveDepartmentWarning && (
              <p className="text-yellow-600">
                Warning: The selected department is inactive. This may affect
                counter availability.
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-4 mt-6 items-center">
          <input
            {...register("status")}
            type="checkbox"
            id="counterStatus"
            className="w-4 h-4"
          />
          <label htmlFor="counterStatus" className="font-medium">
            Active
          </label>
        </div>
        <div className="flex gap-5 justify-end mt-8">
          <Link to={RouteNavigation.counters}>
            <button
              type="button"
              className="flex items-center justify-center h-10 px-6 bg-gray-600 rounded font-semibold text-sm text-white hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </Link>
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center justify-center h-10 px-6 bg-blue-600 rounded font-semibold text-sm text-white hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
          >
            {isLoading
              ? "Processing..."
              : counterData
              ? "Update Counter"
              : "Create Counter"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateCounter;
